/**
 * The journal, and the search that had to move.
 *
 * `server.py` searched with `lower(t.reason) LIKE ?` in SQL. You cannot LIKE a
 * ciphertext, so the filtering happens here, after decryption -- which has a
 * second effect worth naming: the search term never leaves the device. Nobody,
 * including this project, can learn that a parent searched for a child's name.
 *
 * A family writes a few hundred rows a year, so fetching a window and
 * filtering it locally is instant. The server still narrows by MONTH and by
 * PERSON, because those are key-range queries it can do without reading
 * anything it has no business reading -- see the sort key shapes in
 * DATA-MODEL.md.
 */

import { api } from '../api/client';
import { decryptField, encryptField } from '../crypto/familyKey';
import { FAMILY, openReason, sealReason } from '../crypto/content';
import { entryLabel, type Entry, type Person } from './state';

export interface Journal {
  entries: Entry[];
  /** Months that have entries, newest first. Computed before any filtering. */
  months: string[];
  truncated: boolean;
}

interface RawEntry {
  id: string;
  amount: number;
  kind: string;
  date: string;
  voided?: boolean;
  people?: string[];
  shares?: Record<string, number>;
  reasonCt?: string | null;
  reasonKeys?: Record<string, string>;
  voidReasonCt?: string | null;
  autoSaved?: Record<string, Record<string, number>>;
  amended?: Array<{
    amount: number;
    reasonCt?: string | null;
    reasonKeys?: Record<string, string>;
    date: string;
    people?: string[];
    at: string;
  }>;
}

interface RawJournal {
  transactions: RawEntry[];
  months: string[];
  truncated: boolean;
}

async function open(key: CryptoKey, sealed?: string | null): Promise<string> {
  if (!sealed) return '';
  try {
    return await decryptField(key, sealed);
  } catch {
    return '';
  }
}

export async function loadJournal(
  familyId: string,
  key: CryptoKey,
  narrow: { month?: string; person?: string } = {},
): Promise<Journal> {
  const raw = await api.get<RawJournal>(`/v1/families/${familyId}/journal`, {
    ...(narrow.month ? { month: narrow.month } : {}),
    ...(narrow.person ? { person: narrow.person } : {}),
  });

  const entries = await Promise.all(
    raw.transactions.map(async (entry) => ({
      id: entry.id,
      amount: entry.amount,
      kind: entry.kind,
      date: entry.date,
      voided: Boolean(entry.voided),
      people: entry.people ?? [],
      shares: entry.shares ?? {},
      reason: entryLabel(entry.kind, await openReason(key, entry.reasonCt, entry.reasonKeys?.[FAMILY])),
      voidReason: await open(key, entry.voidReasonCt),
      ...(entry.autoSaved ? { autoSaved: entry.autoSaved } : {}),
      corrections: await Promise.all(
        (entry.amended ?? []).map(async (before) => ({
          amount: before.amount,
          // A correction made before the server kept the old wrappings has
          // none, and its old wording cannot be opened; it reads as empty.
          reason: await openReason(key, before.reasonCt, before.reasonKeys?.[FAMILY]).catch(() => ''),
          date: before.date,
          people: before.people ?? [],
          at: before.at,
        })),
      ),
    })),
  );

  return { entries, months: raw.months ?? [], truncated: Boolean(raw.truncated) };
}

/**
 * Filter by what somebody typed.
 *
 * Matches the reason, the correction note and the names of everyone on the
 * row, because "what did we spend on Ravi" is the question people actually
 * ask. Case-insensitive, substring, no ranking -- the same behaviour the SQL
 * LIKE gave, so nobody has to relearn it.
 */
export function search(entries: Entry[], term: string, names: Map<string, string>): Entry[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return entries;
  return entries.filter((entry) => {
    const who = entry.people.map((id) => names.get(id) ?? '').join(' ');
    return `${entry.reason} ${entry.voidReason} ${who}`.toLowerCase().includes(needle);
  });
}

/**
 * Correct an entry in place: amount, reason, who and when.
 *
 * The server keeps what it said before on the row, so a correction is visible
 * rather than silent -- the same bargain as taking one back. The new reason is
 * sealed exactly as a new entry's is: a fresh key, wrapped for the family and
 * for each person now on it, so a child still reads their own row.
 *
 * `amount` is the typed string, already signed by intent (see `signed`).
 */
export async function amendEntry(
  familyId: string,
  key: CryptoKey,
  entryId: string,
  entry: { amount: string; reason: string; people: string[]; date: string },
  roster: Person[] = [],
): Promise<void> {
  const personKeys = new Map<string, CryptoKey>();
  for (const person of roster) {
    if (entry.people.includes(person.id) && person.key) personKeys.set(person.id, person.key);
  }
  const sealed = await sealReason(key, personKeys, entry.reason.trim());
  await api.write('POST', `/v1/families/${familyId}/transactions/${entryId}/amend`, {
    amount: entry.amount,
    reasonCt: sealed.reasonCt,
    reasonKeys: sealed.reasonKeys,
    people: entry.people,
    date: entry.date,
  });
}

/**
 * Take an entry back out.
 *
 * A void, never a delete. Every balance in this app is derived by summing the
 * ledger, so removing a row would silently change history and leave no trace
 * that it had been changed. The row stays, flagged, and the reason is
 * REQUIRED by the API -- a correction nobody explained is worse than the
 * mistake it corrects.
 *
 * The reason is encrypted like any other free text: "took Ravi's off by
 * mistake" is exactly the kind of sentence that carries a child's name.
 */
export async function voidEntry(
  familyId: string,
  key: CryptoKey,
  entryId: string,
  reason: string,
): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/transactions/${entryId}/void`, {
    voidReasonCt: await encryptField(key, reason.trim()),
  });
}
